import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Bars3Icon, XMarkIcon, EyeIcon } from '@heroicons/react/24/outline';
import { CellEntriesPopover } from './CellEntriesPopover';
import type { Entry, Project } from '../../services/api/timeEntry';
import type { PastedEntryData } from './MonthGrid';
import { useTrackedTimeout } from '../../hooks/useTrackedTimeout';
import { isMacPlatform } from '../../utils/platform';
import {
  CELL_STATE_CLASS,
  ROW_STATE_BORDER,
  ROW_STATE_LABEL,
  TARGET_TONE_CLASS,
  dominantRowState,
  formatLoggedVsTarget,
  targetTitle,
  targetTone,
} from './totalsFormat';
import { LEGACY_CZ_NUMBER_LOCALE, compareText } from '../../utils/localeFormat';

interface TimeOffInfo {
  hours: number;
  typeName: string;
}

interface LockedPeriod {
  from: string;
  to: string;
  state: string;
}

interface WeekGridProps {
  weekStart: Date;
  entries: Entry[];
  projects: Project[];
  dailyTotals: Record<string, number>;
  weekTotal: number;
  weekExpectedHours?: number;
  timeOffDays?: Map<string, TimeOffInfo>;
  showAllProjects?: boolean;
  onShowAllProjectsChange?: (show: boolean) => void;
  previousPeriodProjectIds?: Set<number>;
  projectOrder?: number[];
  onProjectOrderChange?: (order: number[]) => void;
  hiddenProjectIds?: Set<number>;
  onHideProject?: (projectId: number) => void;
  onUnhideProject?: (projectId: number) => void;
  lockedPeriods?: LockedPeriod[];
  readOnly?: boolean;
  onEditEntry: (entry: Entry) => void;
  onDeleteEntry?: (entry: Entry) => void;
  onCreateEntry: (date: string, projectId: number, pastedData?: PastedEntryData) => void;
  onMoveEntry?: (entryId: number, newDate: string, newProjectId: number) => void;
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function isDateInLockedPeriod(date: string, lockedPeriods: LockedPeriod[]): LockedPeriod | null {
  for (const period of lockedPeriods) {
    if (date >= period.from && date <= period.to) {
      return period;
    }
  }
  return null;
}

export function WeekGrid({
  weekStart,
  entries,
  projects,
  dailyTotals,
  weekTotal,
  weekExpectedHours,
  timeOffDays = new Map(),
  showAllProjects = false,
  onShowAllProjectsChange,
  previousPeriodProjectIds = new Set(),
  projectOrder = [],
  onProjectOrderChange,
  hiddenProjectIds = new Set(),
  onHideProject,
  onUnhideProject,
  lockedPeriods = [],
  readOnly = false,
  onEditEntry,
  onDeleteEntry,
  onCreateEntry,
  onMoveEntry,
}: WeekGridProps) {
  // Generate dates for the week (using local dates to avoid timezone issues)
  const weekDates = useMemo(() => {
    const dates: string[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    return dates;
  }, [weekStart]);

  // Group entries by project and date (supports multiple entries per cell)
  const entryMap = useMemo(() => {
    const map = new Map<string, Entry[]>();
    for (const entry of entries) {
      const key = `${entry.projectId}-${entry.date}`;
      const existing = map.get(key) || [];
      existing.push(entry);
      map.set(key, existing);
    }
    return map;
  }, [entries]);

  // Get unique projects from entries (same as MonthGrid baseProjects)
  const baseProjects = useMemo(() => {
    const projectIds = new Set<number>();
    const result: Project[] = [];

    for (const entry of entries) {
      if (!projectIds.has(entry.projectId)) {
        projectIds.add(entry.projectId);
        const project = projects.find((p) => p.id === entry.projectId);
        if (project) {
          result.push(project);
        } else {
          result.push({
            id: entry.projectId,
            name: entry.project.name,
            fullName: entry.project.fullName,
            state: 'in_progress',
            isDefault: false,
            mandatoryTasks: false,
            defaultRate: null,
            customer: null,
          });
        }
      }
    }

    for (const project of projects) {
      if (project.isDefault && !projectIds.has(project.id)) {
        projectIds.add(project.id);
        result.push(project);
      }
    }

    return result;
  }, [entries, projects]);

  // IDs of projects with entries in current week
  const currentWeekProjectIds = useMemo(() => {
    const ids = new Set<number>();
    for (const entry of entries) {
      if (entry.hours > 0) {
        ids.add(entry.projectId);
      }
    }
    return ids;
  }, [entries]);

  // Filter projects based on toggle — same logic as MonthGrid
  const filteredProjects = useMemo(() => {
    if (showAllProjects) {
      const projectIds = new Set(baseProjects.map((p) => p.id));
      const allProjects = [...baseProjects];
      for (const project of projects) {
        if (!projectIds.has(project.id)) {
          allProjects.push(project);
        }
      }
      return allProjects;
    }

    const seen = new Set<number>();
    const result = baseProjects.filter((p) => {
      if (currentWeekProjectIds.has(p.id) || previousPeriodProjectIds.has(p.id)) {
        seen.add(p.id);
        return true;
      }
      return false;
    });
    for (const project of projects) {
      if (!seen.has(project.id) && previousPeriodProjectIds.has(project.id)) {
        result.push(project);
      }
    }
    return result;
  }, [baseProjects, projects, showAllProjects, currentWeekProjectIds, previousPeriodProjectIds]);

  // Filter out hidden/zero-hour projects
  const visibleProjects = useMemo(() => {
    return filteredProjects.filter((p) => {
      const hasHours = currentWeekProjectIds.has(p.id);
      if (hasHours) return true;
      if (readOnly) return false;
      if (hiddenProjectIds.has(p.id) && !showAllProjects) return false;
      return true;
    });
  }, [filteredProjects, hiddenProjectIds, currentWeekProjectIds, showAllProjects, readOnly]);

  // Sort projects based on saved order, then alphabetically
  const displayProjects = useMemo(() => {
    const isHiddenDimmed = (p: Project) =>
      showAllProjects && hiddenProjectIds.has(p.id) && !currentWeekProjectIds.has(p.id);

    if (projectOrder.length === 0) {
      return [...visibleProjects].sort((a, b) => {
        const aHidden = isHiddenDimmed(a) ? 1 : 0;
        const bHidden = isHiddenDimmed(b) ? 1 : 0;
        if (aHidden !== bHidden) return aHidden - bHidden;
        const nameA = a.fullName || a.name || '';
        const nameB = b.fullName || b.name || '';
        return compareText(nameA, nameB, LEGACY_CZ_NUMBER_LOCALE);
      });
    }

    const orderMap = new Map(projectOrder.map((id, index) => [id, index]));
    return [...visibleProjects].sort((a, b) => {
      const aHidden = isHiddenDimmed(a) ? 1 : 0;
      const bHidden = isHiddenDimmed(b) ? 1 : 0;
      if (aHidden !== bHidden) return aHidden - bHidden;
      const orderA = orderMap.get(a.id);
      const orderB = orderMap.get(b.id);
      if (orderA !== undefined && orderB !== undefined) return orderA - orderB;
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;
      const nameA = a.fullName || a.name || '';
      const nameB = b.fullName || b.name || '';
      return compareText(nameA, nameB, LEGACY_CZ_NUMBER_LOCALE);
    });
  }, [visibleProjects, projectOrder, showAllProjects, hiddenProjectIds, currentWeekProjectIds]);

  // Drag state for project reordering
  const [draggedId, setDraggedId] = useState<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);

  const handleDragStart = useCallback((e: React.DragEvent, projectId: number) => {
    setDraggedId(projectId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(projectId));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, projectId: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (projectId !== draggedId) {
      setDragOverId(projectId);
    }
  }, [draggedId]);

  const handleDragLeave = useCallback(() => {
    setDragOverId(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    if (draggedId === null || draggedId === targetId) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    const currentOrder = displayProjects.map((p) => p.id);
    const draggedIndex = currentOrder.indexOf(draggedId);
    const targetIndex = currentOrder.indexOf(targetId);
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedId(null);
      setDragOverId(null);
      return;
    }
    const newOrder = [...currentOrder];
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedId);
    onProjectOrderChange?.(newOrder);
    setDraggedId(null);
    setDragOverId(null);
  }, [draggedId, displayProjects, onProjectOrderChange]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
  }, []);

  // Entry drag state
  const [draggedEntry, setDraggedEntry] = useState<Entry | null>(null);
  const [dropTarget, setDropTarget] = useState<{ date: string; projectId: number } | null>(null);

  const handleEntryDragStart = useCallback(
    (e: React.DragEvent, entry: Entry) => {
      if (readOnly || !onMoveEntry) return;
      e.stopPropagation();
      setDraggedEntry(entry);
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', entry.id.toString());
    },
    [readOnly, onMoveEntry]
  );

  const handleCellDragOver = useCallback(
    (e: React.DragEvent, date: string, projectId: number) => {
      if (!draggedEntry || readOnly) return;
      e.preventDefault();
      e.stopPropagation();
      setDropTarget({ date, projectId });
    },
    [draggedEntry, readOnly]
  );

  const handleCellDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleCellDrop = useCallback(
    (e: React.DragEvent, date: string, projectId: number) => {
      e.preventDefault();
      e.stopPropagation();
      if (!draggedEntry || !onMoveEntry) return;
      if (draggedEntry.date !== date || draggedEntry.projectId !== projectId) {
        onMoveEntry(draggedEntry.id, date, projectId);
      }
      setDraggedEntry(null);
      setDropTarget(null);
    },
    [draggedEntry, onMoveEntry]
  );

  const handleEntryDragEnd = useCallback(() => {
    setDraggedEntry(null);
    setDropTarget(null);
  }, []);

  // Popover state for multi-entry cells
  const [popoverState, setPopoverState] = useState<{
    isOpen: boolean;
    date: string;
    projectId: number;
    projectName: string;
    entries: Entry[];
    position: { top: number; left: number };
    isLocked: boolean;
  } | null>(null);

  // Active cell for keyboard navigation
  const [activeCell, setActiveCell] = useState<{ row: number; col: number } | null>(null);
  const cellRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const tableRef = useRef<HTMLDivElement>(null);
  // F1-009: track row-swap focus-follow timeout so unmount / re-keys
  // cannot leave a pending .focus() targeting a stale ref.
  const focusFollow = useTrackedTimeout();

  // Clipboard state for copy/paste
  const [copiedEntry, setCopiedEntry] = useState<PastedEntryData | null>(null);

  const handleCellClick = useCallback(
    (
      e: React.MouseEvent | { currentTarget: HTMLElement },
      date: string,
      projectId: number,
      project: Project,
      isLocked: boolean
    ) => {
      const cellEntries = entryMap.get(`${projectId}-${date}`) || [];

      if (cellEntries.length === 0) {
        if (!readOnly && !isLocked) {
          onCreateEntry(date, projectId);
        }
        return;
      }

      // Has entries - show popover for editing
      const rect = e.currentTarget.getBoundingClientRect();
      setPopoverState({
        isOpen: true,
        date,
        projectId,
        projectName: project.fullName || project.name,
        entries: cellEntries,
        position: {
          top: rect.bottom + 4,
          left: Math.max(8, rect.left - 100),
        },
        isLocked,
      });
    },
    [entryMap, readOnly, onCreateEntry]
  );

  const handlePopoverClose = useCallback(() => {
    setPopoverState(null);
  }, []);

  const handlePopoverEdit = useCallback(
    (entry: Entry) => {
      if (readOnly) return;
      setPopoverState(null);
      onEditEntry(entry);
    },
    [onEditEntry, readOnly]
  );

  const handlePopoverDelete = useCallback(
    (entry: Entry) => {
      if (readOnly || !onDeleteEntry) return;
      setPopoverState(null);
      onDeleteEntry(entry);
    },
    [onDeleteEntry, readOnly]
  );

  const handlePopoverAdd = useCallback(() => {
    if (readOnly || !popoverState) return;
    setPopoverState(null);
    onCreateEntry(popoverState.date, popoverState.projectId);
  }, [popoverState, onCreateEntry, readOnly]);

  // Keyboard navigation handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (displayProjects.length === 0 || weekDates.length === 0) return;

      const maxRow = displayProjects.length - 1;
      const maxCol = weekDates.length - 1;
      const modKey = isMacPlatform() ? e.metaKey : e.ctrlKey;

      // Handle copy/paste with active cell
      if (modKey && activeCell) {
        const project = displayProjects[activeCell.row];
        const date = weekDates[activeCell.col];
        if (!project || !date) return;

        if (e.key === 'c' || e.key === 'C') {
          e.preventDefault();
          e.stopPropagation();
          const cellEntries = entryMap.get(`${project.id}-${date}`) || [];
          const firstEntry = cellEntries[0];
          if (firstEntry) {
            setCopiedEntry({
              hours: firstEntry.hours,
              description: firstEntry.description,
              task: firstEntry.task,
            });
          }
          const cellKey = `${activeCell.row}-${activeCell.col}`;
          cellRefs.current.get(cellKey)?.focus();
          return;
        }

        if (e.key === 'v' || e.key === 'V') {
          e.preventDefault();
          e.stopPropagation();
          if (copiedEntry && !readOnly) {
            const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
            const isLocked = !!lockedPeriod && lockedPeriod.state !== 'rejected';
            if (!isLocked) {
              onCreateEntry(date, project.id, copiedEntry);
            }
          }
          return;
        }
      }

      // Handle backspace/delete/d to remove entries
      if ((e.key === 'Backspace' || e.key === 'Delete' || e.key === 'd' || e.key === 'D') && activeCell && onDeleteEntry && !readOnly) {
        const project = displayProjects[activeCell.row];
        const date = weekDates[activeCell.col];
        if (!project || !date) return;

        const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
        const isLocked = !!lockedPeriod && lockedPeriod.state !== 'rejected';
        if (isLocked) return;

        const cellEntries = entryMap.get(`${project.id}-${date}`) || [];
        const deletableEntries = cellEntries.filter(entry => entry.state === 'open' || entry.state === 'rejected');

        if (deletableEntries.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          for (const entry of deletableEntries) {
            onDeleteEntry(entry);
          }
        }
        return;
      }

      // If no active cell, set to first cell on any arrow key
      if (!activeCell) {
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          e.preventDefault();
          setActiveCell({ row: 0, col: 0 });
          cellRefs.current.get('0-0')?.focus();
        }
        return;
      }

      // Shift+Arrow Up/Down: move current row
      if (e.shiftKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
        const targetRow = e.key === 'ArrowUp' ? activeCell.row - 1 : activeCell.row + 1;
        if (targetRow < 0 || targetRow > maxRow) return;

        const currentOrder = displayProjects.map((p) => p.id);
        const newOrder = [...currentOrder];
        const tmp = newOrder[activeCell.row]!;
        newOrder[activeCell.row] = newOrder[targetRow]!;
        newOrder[targetRow] = tmp;

        onProjectOrderChange?.(newOrder);
        setActiveCell({ row: targetRow, col: activeCell.col });
        focusFollow.schedule(() => {
          cellRefs.current.get(`${targetRow}-${activeCell.col}`)?.focus();
        }, 0);
        return;
      }

      let newRow = activeCell.row;
      let newCol = activeCell.col;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          newRow = Math.max(0, activeCell.row - 1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          newRow = Math.min(maxRow, activeCell.row + 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          newCol = Math.max(0, activeCell.col - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          newCol = Math.min(maxCol, activeCell.col + 1);
          break;
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const project = displayProjects[activeCell.row];
          const date = weekDates[activeCell.col];
          if (!project || !date) return;
          const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
          const isLocked = !!lockedPeriod && lockedPeriod.state !== 'rejected';
          const cellEl = cellRefs.current.get(`${activeCell.row}-${activeCell.col}`);
          if (!cellEl) return;
          handleCellClick({ currentTarget: cellEl }, date, project.id, project, isLocked);
          return;
        }
        case 'Escape':
          e.preventDefault();
          setActiveCell(null);
          return;
        default:
          return;
      }

      if (newRow !== activeCell.row || newCol !== activeCell.col) {
        setActiveCell({ row: newRow, col: newCol });
        cellRefs.current.get(`${newRow}-${newCol}`)?.focus();
      }
    },
    [activeCell, displayProjects, weekDates, entryMap, copiedEntry, onCreateEntry, onDeleteEntry, readOnly, lockedPeriods, handleCellClick, onProjectOrderChange, focusFollow]
  );

  // Attach keyboard listener
  useEffect(() => {
    const container = tableRef.current;
    if (!container) return;
    container.addEventListener('keydown', handleKeyDown as EventListener);
    return () => {
      container.removeEventListener('keydown', handleKeyDown as EventListener);
    };
  }, [handleKeyDown]);

  const formatDayHeader = (dateStr: string, dayName: string) => {
    const dayNum = parseInt(dateStr.split('-')[2] || '1', 10);
    const todayDate = new Date();
    const todayStr = `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
    const isToday = dateStr === todayStr;
    const dateObj = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = dateObj.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    return (
      <div className={`text-center ${isToday ? 'text-blue-600' : isWeekend ? 'text-gray-400' : ''}`}>
        <div className="text-[10px]">{dayName}</div>
        <div className={`text-xs ${isToday ? 'bg-blue-600 text-white rounded-full w-5 h-5 mx-auto flex items-center justify-center' : ''}`}>
          {dayNum}
        </div>
      </div>
    );
  };

  return (
    <div className="overflow-auto max-h-[calc(100vh-12rem)] relative outline-none" ref={tableRef} tabIndex={0}>
      <table className="w-full border-collapse text-xs">
        <thead className="sticky top-0 z-20">
          <tr className="bg-white">
            <th className="py-1 px-2 text-left font-medium text-gray-700 w-[280px] max-w-[280px] sticky left-0 bg-white z-30">
              <div className="flex items-center justify-between">
                <span>Project</span>
                <label className={`flex items-center gap-1 text-[10px] font-normal cursor-pointer ${
                  displayProjects.length === 0 && !showAllProjects
                    ? 'text-blue-600 font-medium'
                    : 'text-gray-500'
                }`}>
                  <input
                    type="checkbox"
                    checked={showAllProjects}
                    onChange={(e) => onShowAllProjectsChange?.(e.target.checked)}
                    className={`h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${
                      displayProjects.length === 0 && !showAllProjects
                        ? 'animate-pulse ring-2 ring-blue-400 ring-offset-1'
                        : ''
                    }`}
                  />
                  All
                </label>
              </div>
            </th>
            {weekDates.map((date, index) => (
              <th key={date} className="py-1 px-1 bg-white">
                {formatDayHeader(date, DAYS[index] ?? '')}
              </th>
            ))}
            <th className="py-1 px-2 text-right font-medium text-gray-700 sticky right-0 bg-white z-30 w-24">
              Total
            </th>
          </tr>
          <tr className="border-b border-gray-200">
            <td colSpan={weekDates.length + 2}></td>
          </tr>
        </thead>
        <tbody>
          {/* Time Off pinned as first row */}
          {weekDates.some((date) => timeOffDays.has(date)) && (() => {
            const timeOffTotal = weekDates.reduce((sum, date) => {
              const info = timeOffDays.get(date);
              return sum + (info ? info.hours : 0);
            }, 0);
            return (
              <tr className="border-b-2 border-indigo-200 bg-indigo-50">
                <td className="py-1 px-2 font-medium text-indigo-700 text-xs sticky left-0 bg-indigo-50 z-10">
                  Time Off
                </td>
                {weekDates.map((date) => {
                  const timeOff = timeOffDays.get(date);
                  return (
                    <td key={date} className="py-0.5 px-1">
                      {timeOff && (
                        <div
                          className="w-full h-7 flex items-center justify-center text-[10px] font-medium bg-indigo-200 text-indigo-800 rounded-sm"
                          title={`${timeOff.typeName}: ${timeOff.hours}h`}
                        >
                          {timeOff.hours}
                        </div>
                      )}
                    </td>
                  );
                })}
                <td className="py-1 px-2 text-right font-medium text-indigo-700 text-xs sticky right-0 bg-indigo-50 z-10">
                  {timeOffTotal > 0 ? timeOffTotal.toFixed(1) : '-'}
                </td>
              </tr>
            );
          })()}
          {displayProjects.map((project, rowIndex) => {
            const rowEntries = weekDates.flatMap(
              (date) => entryMap.get(`${project.id}-${date}`) ?? [],
            );
            const projectTotal = rowEntries.reduce((sum, e) => sum + e.hours, 0);
            // MKS-315 — row state as a thin left border, see MonthGrid.
            const rowState = dominantRowState(rowEntries);
            const isDragging = draggedId === project.id;
            const isDragOver = dragOverId === project.id;
            const isEvenRow = rowIndex % 2 === 0;
            const rowBg = isEvenRow ? 'bg-white' : 'bg-gray-100';
            const isHiddenDimmed = showAllProjects && hiddenProjectIds.has(project.id) && !currentWeekProjectIds.has(project.id);

            return (
              <tr
                key={project.id}
                onDragOver={readOnly ? undefined : (e) => handleDragOver(e, project.id)}
                onDragLeave={readOnly ? undefined : handleDragLeave}
                onDrop={readOnly ? undefined : (e) => handleDrop(e, project.id)}
                className={`group/row border-b border-gray-100 hover:bg-blue-50/50 ${rowBg} ${
                  isDragging ? 'opacity-50' : ''
                } ${isDragOver ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
              >
                <td
                  draggable={!readOnly}
                  onDragStart={readOnly ? undefined : (e) => handleDragStart(e, project.id)}
                  onDragEnd={readOnly ? undefined : handleDragEnd}
                  className={`py-1 px-2 w-[280px] max-w-[280px] sticky left-0 z-10 ${isEvenRow ? 'bg-white' : 'bg-gray-100'} ${
                    rowState ? `border-l-2 ${ROW_STATE_BORDER[rowState]}` : ''
                  }`}
                  title={project.fullName || project.name}
                >
                  <div className="flex items-center gap-1">
                    {rowState && <span className="sr-only">{ROW_STATE_LABEL[rowState]}</span>}
                    {!readOnly && (
                      <Bars3Icon className="w-3 h-3 text-gray-400 cursor-grab flex-shrink-0" />
                    )}
                    <span className={`font-medium truncate block ${isHiddenDimmed ? 'text-gray-400' : 'text-gray-900'}`}>
                      {project.fullName || project.name}
                    </span>
                    {!readOnly && isHiddenDimmed && onUnhideProject && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onUnhideProject(project.id); }}
                        className="ml-auto flex-shrink-0 p-0.5 text-gray-400 hover:text-blue-600 rounded transition-colors"
                        title="Show project"
                      >
                        <EyeIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {!readOnly && projectTotal === 0 && !hiddenProjectIds.has(project.id) && onHideProject && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onHideProject(project.id); }}
                        className="ml-auto flex-shrink-0 p-0.5 text-gray-300 hover:text-red-500 rounded transition-colors opacity-0 group-hover/row:opacity-100"
                        title="Hide project"
                      >
                        <XMarkIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>
                {weekDates.map((date, colIndex) => {
                  const cellEntries = entryMap.get(`${project.id}-${date}`) || [];
                  const cellTotal = cellEntries.reduce((s, e) => s + e.hours, 0);
                  const hasMultiple = cellEntries.length > 1;
                  const dateObj = new Date(date + 'T00:00:00');
                  const dayOfWeek = dateObj.getDay();
                  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                  const lockedPeriod = isDateInLockedPeriod(date, lockedPeriods);
                  const isLocked = !!lockedPeriod && lockedPeriod.state !== 'rejected';
                  const allEditable = cellEntries.every((e) => e.state === 'open' || e.state === 'rejected');
                  const isDropTargetCell = dropTarget?.date === date && dropTarget?.projectId === project.id;
                  const isDraggingThis = draggedEntry && cellEntries.some(e => e.id === draggedEntry.id);
                  const canDrag = !readOnly && !isLocked && cellEntries.length === 1 && allEditable && !!onMoveEntry;
                  const draggableEntry = canDrag ? cellEntries[0] : undefined;
                  const isActiveCell = activeCell?.row === rowIndex && activeCell?.col === colIndex;
                  const cellKey = `${rowIndex}-${colIndex}`;
                  // MKS-315 — lighter state fill, empty cells never painted.
                  const cellState = dominantRowState(cellEntries);
                  const stateClass = cellState ? CELL_STATE_CLASS[cellState] : '';

                  return (
                    <td
                      key={date}
                      className={`py-0.5 px-0.5 overflow-visible relative ${isWeekend ? 'bg-gray-50/50' : ''}`}
                      onDragOver={draggedEntry ? (e) => handleCellDragOver(e, date, project.id) : undefined}
                      onDragLeave={draggedEntry ? handleCellDragLeave : undefined}
                      onDrop={draggedEntry ? (e) => handleCellDrop(e, date, project.id) : undefined}
                    >
                      <button
                        ref={(el) => {
                          if (el) cellRefs.current.set(cellKey, el);
                          else cellRefs.current.delete(cellKey);
                        }}
                        tabIndex={isActiveCell ? 0 : -1}
                        draggable={canDrag}
                        onDragStart={draggableEntry ? (e) => handleEntryDragStart(e, draggableEntry) : undefined}
                        onDragEnd={canDrag ? handleEntryDragEnd : undefined}
                        onClick={() => {
                          setActiveCell({ row: rowIndex, col: colIndex });
                        }}
                        onDoubleClick={(e) => {
                          handleCellClick(e, date, project.id, project, isLocked);
                        }}
                        onFocus={() => setActiveCell({ row: rowIndex, col: colIndex })}
                        disabled={(isLocked || readOnly) && cellEntries.length === 0}
                        className={`
                          w-full h-7 text-center text-xs rounded-sm transition-colors relative overflow-visible
                          ${cellEntries.length > 0 ? 'font-medium' : ''}
                          ${stateClass}
                          ${cellEntries.length === 0 && !isLocked && !readOnly ? 'hover:bg-gray-100' : ''}
                          ${(isLocked || readOnly) && cellEntries.length === 0 ? 'cursor-default' : 'cursor-pointer'}
                          ${isDropTargetCell && !isLocked ? 'ring-2 ring-green-500 bg-green-50' : ''}
                          ${isDraggingThis ? 'opacity-50' : ''}
                          ${canDrag ? 'cursor-grab active:cursor-grabbing' : ''}
                          ${isActiveCell ? 'ring-2 ring-indigo-500 ring-offset-1 z-30' : ''}
                        `}
                        title={cellEntries.length > 0
                          ? cellEntries.map(e => `${e.hours}h${e.task ? ` - ${e.task}` : ''}${e.state === 'rejected' && e.rejectionReason ? ` [Rejected: ${e.rejectionReason}]` : ''}${e.description ? `: ${e.description}` : ''}`).join('\n')
                          : isLocked ? `${date} (${lockedPeriod?.state})` : date}
                      >
                        {cellTotal > 0 ? cellTotal : ''}
                        {hasMultiple && (
                          <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] rounded-full w-3 h-3 flex items-center justify-center leading-none font-medium z-20">
                            {cellEntries.length}
                          </span>
                        )}
                      </button>
                    </td>
                  );
                })}
                <td className={`py-1 px-2 text-right font-medium text-gray-900 text-xs sticky right-0 z-10 ${isEvenRow ? 'bg-white' : 'bg-gray-100'}`}>
                  {projectTotal > 0 ? projectTotal.toFixed(1) : '-'}
                </td>
              </tr>
            );
          })}

          {displayProjects.length === 0 && (
            <tr>
              <td colSpan={9} className="py-6 text-center text-gray-500 text-sm">
                {!showAllProjects && baseProjects.length > 0
                  ? 'No active projects. Check "All" to see projects assigned to everyone.'
                  : 'No projects assigned. Contact your manager to get assigned to projects.'}
              </td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-200 bg-gray-50">
            <td className="py-1 px-2 font-semibold text-gray-900 text-xs sticky left-0 bg-gray-50 z-10">
              Daily Total
            </td>
            {weekDates.map((date) => (
              <td key={date} className="py-1 px-1 text-center font-semibold text-xs text-gray-900">
                {dailyTotals[date] ? dailyTotals[date].toFixed(1) : '-'}
              </td>
            ))}
            {/* MKS-315 — same logged-vs-target shape as the month grid's
                Weekly row, so a week reads identically in both views. */}
            <td
              className={`py-1 px-2 text-right font-bold text-xs sticky right-0 bg-gray-50 z-10 ${
                weekExpectedHours !== undefined
                  ? `whitespace-nowrap ${TARGET_TONE_CLASS[targetTone(weekTotal, weekExpectedHours)]}`
                  : 'text-blue-600'
              }`}
              title={
                weekExpectedHours !== undefined ? targetTitle(weekTotal, weekExpectedHours) : undefined
              }
            >
              {weekExpectedHours !== undefined
                ? formatLoggedVsTarget(weekTotal, weekExpectedHours)
                : weekTotal.toFixed(1)}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Popover for cells with multiple entries */}
      {popoverState?.isOpen && (
        <CellEntriesPopover
          entries={popoverState.entries}
          projectName={popoverState.projectName}
          date={popoverState.date}
          isLocked={popoverState.isLocked}
          position={popoverState.position}
          onEdit={handlePopoverEdit}
          onDelete={handlePopoverDelete}
          onAdd={handlePopoverAdd}
          onClose={handlePopoverClose}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}
